import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLang } from '../lib/useLang'
import { RoutesEnum } from '../lib/routes'
import './impact-cases.css'

/* Case ids + their visual type + teaser icon. Content comes from locales. */
const CASE_IDS = [
  { id: 'ltv',      visualType: 'allocation', teaserIcon: 'up' },
  { id: 'risk',     visualType: 'risk',       teaserIcon: 'down' },
  { id: 'leads',    visualType: 'funnel',     teaserIcon: 'up' },
  { id: 'forecast', visualType: 'forecast',   teaserIcon: 'down' },
  { id: 'contact',  visualType: 'contact',    teaserIcon: 'clock' },
]

const METRIC_ICONS = ['up', 'down', 'usd', 'clock']

/* ── Icons ───────────────────────────────────────── */
function Icon({ type }) {
  switch (type) {
    case 'up':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M7 17L17 7M9 7h8v8' />
        </svg>
      )
    case 'down':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M7 7l10 10M15 17H7V9' />
        </svg>
      )
    case 'usd':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6' />
        </svg>
      )
    case 'clock':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <circle cx='12' cy='12' r='9' />
          <path d='M12 7v5l3 2' />
        </svg>
      )
    default:
      return null
  }
}

/* ── Number count-up ───────────────────────────── */
function useCountUp(target, run, durationMs = 900) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (!run) {
      setValue(0)
      return
    }
    let raf, start
    const tick = (t) => {
      if (!start) start = t
      const p = Math.min(1, (t - start) / durationMs)
      const eased = 1 - Math.pow(1 - p, 3)
      setValue(target * eased)
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => raf && cancelAnimationFrame(raf)
  }, [target, run, durationMs])
  return value
}

function parseMetric(rawValue) {
  const s = String(rawValue)
  const m = s.match(/^([−+]?)([\d.,]+)([^\d]*)$/)
  if (!m) return null
  const sign = m[1] === '−' ? '-' : m[1]
  const n = parseFloat(m[2].replace(',', '.'))
  return { prefix: sign, n, suffix: m[3] || '' }
}

function MetricCard({ metric, run, tone, iconType }) {
  const parsed = parseMetric(metric.value)
  const animated = useCountUp(parsed ? parsed.n : 0, run && !!parsed)
  let display = metric.value
  if (parsed) {
    const isFloat = String(parsed.n).includes('.') && parsed.n < 100
    const num = isFloat ? animated.toFixed(1) : Math.round(animated)
    const signOut = parsed.prefix === '-' ? '−' : parsed.prefix
    display = `${signOut}${num}${parsed.suffix}`
  }
  const arrow = iconType === 'up' ? '↑' : iconType === 'down' ? '↓' : null
  return (
    <div className={`impact-metric impact-metric--${tone}`}>
      <div className='impact-metric__head'>
        <Icon type={iconType} />
        <span className='impact-metric__arrow' aria-hidden='true'>{arrow}</span>
      </div>
      <div className='impact-metric__value'>{display}</div>
      <div className='impact-metric__label'>{metric.label}</div>
      <div className='impact-metric__desc'>{metric.desc}</div>
    </div>
  )
}

/* ── Visualizations ────────────────────────────── */
function AllocationVisual({ run, L }) {
  const before = [
    { name: L.lowValue,    pct: 48, tone: 'beige' },
    { name: L.mediumValue, pct: 34, tone: 'beige' },
    { name: L.highValue,   pct: 18, tone: 'mint' },
  ]
  const after = [
    { name: L.lowValue,    pct: 18, tone: 'beige' },
    { name: L.mediumValue, pct: 36, tone: 'mint' },
    { name: L.highValue,   pct: 46, tone: 'mint' },
  ]
  return (
    <div className='impact-visual impact-visual--allocation'>
      <div className='impact-visual__head'>
        <span className='impact-visual__title'>{L.budgetAllocation}</span>
        <span className='impact-visual__legend'>
          <i className='impact-visual__dot impact-visual__dot--beige' /> {L.genericSpend}
          <i className='impact-visual__dot impact-visual__dot--mint' /> {L.ltvSpend}
        </span>
      </div>
      <div className='impact-visual__cmp'>
        <Stack label={L.before} rows={before} run={run} />
        <div className='impact-visual__arrow' aria-hidden='true'>→</div>
        <Stack label={L.after} rows={after} run={run} highlight />
      </div>
    </div>
  )
}

function Stack({ label, rows, run, highlight }) {
  return (
    <div className='impact-stack'>
      <div className={`impact-stack__label ${highlight ? 'is-highlight' : ''}`}>{label}</div>
      <div className='impact-stack__bars'>
        {rows.map((r, i) => (
          <div key={i} className='impact-stack__row'>
            <span className='impact-stack__row-name'>{r.name}</span>
            <div className='impact-stack__row-track'>
              <div
                className={`impact-stack__row-fill impact-stack__row-fill--${r.tone}`}
                style={{
                  width: run ? `${r.pct}%` : '0%',
                  transition: `width 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.12}s`,
                }}
              />
              <span className='impact-stack__row-val'>{r.pct}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function RiskVisual({ run, L }) {
  const buckets = [
    { name: L.lowRisk,    before: 38, after: 56, tone: 'mint' },
    { name: L.mediumRisk, before: 36, after: 30, tone: 'beige' },
    { name: L.highRisk,   before: 26, after: 14, tone: 'beige' },
  ]
  return (
    <div className='impact-visual impact-visual--risk'>
      <div className='impact-visual__head'>
        <span className='impact-visual__title'>{L.portfolioComposition}</span>
      </div>
      <div className='impact-visual__cmp impact-visual__cmp--three'>
        {buckets.map((b, i) => (
          <div key={i} className='impact-bucket'>
            <div className='impact-bucket__name'>{b.name}</div>
            <div className='impact-bucket__chart'>
              <div className='impact-bucket__col'>
                <span className='impact-bucket__col-label'>{L.before}</span>
                <div className='impact-bucket__bar'>
                  <span
                    className={`impact-bucket__fill impact-bucket__fill--${b.tone}`}
                    style={{
                      height: run ? `${b.before * 1.4}%` : '0%',
                      transition: `height 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.1}s`,
                    }}
                  />
                  <span className='impact-bucket__val'>{b.before}%</span>
                </div>
              </div>
              <div className='impact-bucket__col'>
                <span className='impact-bucket__col-label is-highlight'>{L.after}</span>
                <div className='impact-bucket__bar'>
                  <span
                    className={`impact-bucket__fill impact-bucket__fill--${b.tone === 'mint' ? 'mint-strong' : b.tone}`}
                    style={{
                      height: run ? `${b.after * 1.4}%` : '0%',
                      transition: `height 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.1 + 0.2}s`,
                    }}
                  />
                  <span className='impact-bucket__val'>{b.after}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FunnelVisual({ run, L }) {
  const stages = [
    { name: L.leads,         before: 100, after: 100 },
    { name: L.qualified,     before: 42,  after: 58 },
    { name: L.opportunities, before: 20,  after: 31 },
    { name: L.deals,         before: 8,   after: 14 },
  ]
  return (
    <div className='impact-visual impact-visual--funnel'>
      <div className='impact-visual__head'>
        <span className='impact-visual__title'>{L.funnelTitle}</span>
      </div>
      <div className='impact-funnel'>
        {stages.map((s, i) => (
          <div key={i} className='impact-funnel__row'>
            <div className='impact-funnel__name'>{s.name}</div>
            <div className='impact-funnel__pair'>
              <div
                className='impact-funnel__bar impact-funnel__bar--beige'
                style={{
                  width: run ? `${s.before}%` : '0%',
                  transition: `width 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.08}s`,
                }}
              >
                <span>{s.before}</span>
              </div>
              <div
                className='impact-funnel__bar impact-funnel__bar--mint'
                style={{
                  width: run ? `${s.after}%` : '0%',
                  transition: `width 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.08 + 0.18}s`,
                }}
              >
                <span>{s.after}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ForecastVisual({ run, L }) {
  const W = 480, H = 180
  const actual = '20,140 60,120 100,128 140,100 180,108 220,84 260,90 300,70 340,82 380,60 420,68 460,50'
  const predicted = '20,138 60,124 100,126 140,104 180,106 220,86 260,88 300,72 340,78 380,62 420,64 460,52'
  return (
    <div className='impact-visual impact-visual--forecast'>
      <div className='impact-visual__head'>
        <span className='impact-visual__title'>{L.forecastTitle}</span>
        <span className='impact-visual__legend'>
          <i className='impact-visual__dot impact-visual__dot--beige' /> {L.actual}
          <i className='impact-visual__dot impact-visual__dot--mint' /> {L.forecast}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className='impact-forecast-svg' preserveAspectRatio='none'>
        {[0, 1, 2, 3].map(i => (
          <line key={i} x1={0} x2={W} y1={30 + i * 40} y2={30 + i * 40} stroke='rgba(30,30,30,0.06)' strokeDasharray='3 5' />
        ))}
        <polyline
          points={actual}
          fill='none'
          stroke='#9BCBB4'
          strokeWidth='2.5'
          strokeLinecap='round'
          strokeLinejoin='round'
          style={{ strokeDasharray: 900, strokeDashoffset: run ? 0 : 900, transition: 'stroke-dashoffset 1.2s ease 0.1s' }}
        />
        <polyline
          points={predicted}
          fill='none'
          stroke='#E6D4BD'
          strokeWidth='2.5'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeDasharray='4 4'
          style={{ strokeDashoffset: run ? 0 : 900, transition: 'stroke-dashoffset 1.2s ease 0.4s' }}
        />
      </svg>
    </div>
  )
}

function ContactVisual({ run, L }) {
  const rows = 4
  const cols = 8
  const heat = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      const center = (c >= 3 && c <= 5) ? 1 : 0.3
      const rowBias = (r === 1 || r === 2) ? 1 : 0.6
      return Math.min(1, center * rowBias * (0.55 + Math.random() * 0.45))
    })
  )
  return (
    <div className='impact-visual impact-visual--contact'>
      <div className='impact-visual__head'>
        <span className='impact-visual__title'>{L.contactTitle}</span>
        <span className='impact-visual__legend'>
          <i className='impact-visual__dot impact-visual__dot--beige' /> {L.low}
          <i className='impact-visual__dot impact-visual__dot--mint' /> {L.high}
        </span>
      </div>
      <div className='impact-heatmap'>
        {heat.map((row, ri) => (
          <div key={ri} className='impact-heatmap__row'>
            {row.map((cell, ci) => {
              const isHot = cell > 0.7
              const isMid = cell > 0.45 && cell <= 0.7
              const cls = isHot ? 'is-hot' : isMid ? 'is-mid' : 'is-cold'
              return (
                <span
                  key={ci}
                  className={`impact-heatmap__cell ${cls}`}
                  style={{
                    opacity: run ? 1 : 0,
                    transitionDelay: `${(ri * cols + ci) * 18}ms`,
                  }}
                />
              )
            })}
          </div>
        ))}
      </div>
      <div className='impact-heatmap__axis'>
        <span>09</span><span>11</span><span>13</span><span>15</span><span>17</span><span>19</span>
      </div>
    </div>
  )
}

function CaseVisual({ visualType, run, labels }) {
  switch (visualType) {
    case 'allocation': return <AllocationVisual run={run} L={labels} />
    case 'risk':       return <RiskVisual run={run} L={labels} />
    case 'funnel':     return <FunnelVisual run={run} L={labels} />
    case 'forecast':   return <ForecastVisual run={run} L={labels} />
    case 'contact':    return <ContactVisual run={run} L={labels} />
    default: return null
  }
}

/* ── Main section ──────────────────────────────── */
export default function ImpactCases() {
  const { t } = useLang()
  const navigate = useNavigate()
  const [activeId, setActiveId] = useState(null)
  const [revealMetrics, setRevealMetrics] = useState(false)
  const resultRef = useRef(null)

  useEffect(() => {
    if (!activeId) return
    setRevealMetrics(false)
    const t1 = setTimeout(() => setRevealMetrics(true), 120)
    const t2 = setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [activeId])

  const cases = CASE_IDS.map((c) => ({
    ...c,
    data: t(`impact.cases.${c.id}`),
  }))

  const activeMeta = CASE_IDS.find((c) => c.id === activeId) || null
  const activeData = activeMeta ? t(`impact.cases.${activeMeta.id}`) : null

  const labels = t('impact.labels') || {}

  const handleKey = (id, e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setActiveId(id)
    }
  }

  return (
    <section id='impact' className='impact-section'>
      <div className='impact-section__inner'>
        <header className='impact-section__head' data-reveal>
          <h2 className='impact-section__title'>{t('impact.title')}</h2>
          <div className='impact-section__sub'>{t('impact.subtitle')}</div>
          <p className='impact-section__desc'>{t('impact.description')}</p>
        </header>

        {!activeData ? (
          <div className='impact-cards'>
            {cases.map((c, idx) => {
              const tone = idx % 2 === 0 ? 'sand' : 'mint'
              const d = c.data || {}
              return (
                <button
                  key={c.id}
                  type='button'
                  className={`impact-card impact-card--${tone}`}
                  onClick={() => setActiveId(c.id)}
                  onKeyDown={(e) => handleKey(c.id, e)}
                  style={{ '--i': idx }}
                  aria-label={`${t('impact.openCase')}: ${d.title || ''}`}
                >
                  <div className='impact-card__head'>
                    <span className='impact-card__category'>{d.category}</span>
                    <span className='impact-card__icon' aria-hidden='true'>
                      <Icon type={c.teaserIcon} />
                    </span>
                  </div>
                  <h3 className='impact-card__title'>{d.title}</h3>
                  <div className='impact-card__client'>{d.client}</div>
                  <div className='impact-card__teaser'>
                    <span className='impact-card__metric'>{d.teaserMetric}</span>
                    <span className='impact-card__teaser-line'>{d.teaserLine}</span>
                  </div>
                  <div className='impact-card__cta' aria-hidden='true'>
                    {t('impact.openCase')}
                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M5 12h14M13 5l7 7-7 7' />
                    </svg>
                  </div>
                </button>
              )
            })}

            {/* Placeholder slot — your task goes here */}
            <button
              type='button'
              className='impact-card impact-card--placeholder'
              onClick={() => navigate(RoutesEnum.CONTACT)}
              style={{ '--i': cases.length }}
              aria-label={t('impact.placeholderTitle')}
            >
              <div className='impact-card__head'>
                <span className='impact-card__category'>{t('impact.placeholderCategory')}</span>
                <span className='impact-card__icon' aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <line x1='12' y1='5' x2='12' y2='19' />
                    <line x1='5' y1='12' x2='19' y2='12' />
                  </svg>
                </span>
              </div>
              <h3 className='impact-card__title'>{t('impact.placeholderTitle')}</h3>
              <div className='impact-card__client'>{t('impact.placeholderClient')}</div>
              <div className='impact-card__teaser'>
                <span className='impact-card__metric'>{t('impact.placeholderTeaser')}</span>
                <span className='impact-card__teaser-line'>{t('impact.placeholderLine')}</span>
              </div>
              <div className='impact-card__cta' aria-hidden='true'>
                {t('impact.placeholderCta')}
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M5 12h14M13 5l7 7-7 7' />
                </svg>
              </div>
            </button>
          </div>
        ) : (
          <div ref={resultRef} className='impact-result' key={activeMeta.id}>
            <button
              type='button'
              className='impact-back'
              onClick={() => setActiveId(null)}
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M19 12H5M12 19l-7-7 7-7' />
              </svg>
              {t('impact.back')}
            </button>

            <div className='impact-result__head'>
              <div className='impact-result__category'>{activeData.category}</div>
              <h3 className='impact-result__title'>{activeData.title}</h3>
              <div className='impact-result__client'>{activeData.client}</div>
              <p className='impact-result__narrative'>{activeData.narrative}</p>
            </div>

            <div className='impact-result__metrics'>
              {(activeData.metrics || []).map((m, i) => (
                <div
                  key={i}
                  className='impact-metric-wrap'
                  style={{
                    transitionDelay: `${i * 90}ms`,
                    transform: revealMetrics ? 'translateY(0)' : 'translateY(12px)',
                    opacity: revealMetrics ? 1 : 0,
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                  }}
                >
                  <MetricCard
                    metric={m}
                    run={revealMetrics}
                    tone={i % 2 === 0 ? 'sand' : 'mint'}
                    iconType={METRIC_ICONS[i] || 'up'}
                  />
                </div>
              ))}
            </div>

            <div className='impact-result__visual'>
              <CaseVisual visualType={activeMeta.visualType} run={revealMetrics} labels={labels} />
            </div>

            <div className='impact-result__outcome'>
              <span className='impact-result__outcome-label'>{t('impact.outcomeLabel')}</span>
              <p>{activeData.outcome}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
